import {
  emailClient,
  isTerminalDraft,
  type EmailDraftRevision,
  type EmailSetupInput,
} from "@email/emailClient";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export const emailKeys = {
  all: ["email"] as const,
  principal: (principal: string) => ["email", principal] as const,
  setup: (principal: string) => ["email", principal, "setup"] as const,
  assistant: (principal: string) => ["email", principal, "assistant"] as const,
  draft: (principal: string, uuid?: string) =>
    ["email", principal, "draft", uuid] as const,
  message: (principal: string, uuid?: string) =>
    ["email", principal, "message", uuid] as const,
  conversations: (principal: string, inbox: boolean) =>
    ["email", principal, "conversations", inbox] as const,
  conversation: (
    principal: string,
    kind?: "draft" | "message",
    uuid?: string,
  ) => ["email", principal, "conversation", kind, uuid] as const,
};

export const useEmailSetup = (principalKey: string) =>
  useQuery({
    queryKey: emailKeys.setup(principalKey),
    queryFn: emailClient.getSetup,
    retry: false,
  });

export const useUpdateEmailSetup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmailSetupInput) => emailClient.updateSetup(input),
    meta: { handled: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: emailKeys.all }),
  });
};

export const useStartEmailOauth = () =>
  useMutation({
    mutationFn: emailClient.startOauth,
    meta: { handled: true },
  });

export const useDisconnectEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: emailClient.disconnect,
    meta: { handled: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: emailKeys.all }),
  });
};

export const useApproveEmailDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, revision }: { uuid: string; revision: number }) =>
      emailClient.approveDraft(uuid, revision),
    meta: { handled: true },
    onSettled: () => queryClient.invalidateQueries({ queryKey: emailKeys.all }),
  });
};

export const useReviseEmailDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, body }: { uuid: string; body: EmailDraftRevision }) =>
      emailClient.reviseDraft(uuid, body),
    meta: { handled: true },
    onSettled: () => queryClient.invalidateQueries({ queryKey: emailKeys.all }),
  });
};

export const useEmailDraft = (principal: string, uuid?: string) =>
  useQuery({
    queryKey: emailKeys.draft(principal, uuid),
    queryFn: () => emailClient.getDraft(uuid!),
    enabled: !!uuid,
    retry: false,
    refetchInterval: (query) =>
      isTerminalDraft(query.state.data?.status) ? false : 5000,
  });
export const useEmailMessage = (principal: string, uuid?: string) =>
  useQuery({
    queryKey: emailKeys.message(principal, uuid),
    queryFn: () => emailClient.getMessage(uuid!),
    enabled: !!uuid,
    retry: false,
    staleTime: 60000,
  });

export const useEmailConversations = (
  principal: string,
  inbox: boolean,
  enabled: boolean,
) =>
  useInfiniteQuery({
    queryKey: emailKeys.conversations(principal, inbox),
    initialPageParam: 0,
    queryFn: ({ pageParam }) => emailClient.listConversations(inbox, pageParam),
    getNextPageParam: (page) => page.next_offset ?? undefined,
    select: (data) => ({
      items: [
        ...new Map(
          data.pages
            .flatMap((page) => page.items)
            .map((item) => [item.key, item]),
        ).values(),
      ],
      total: data.pages[0].total,
    }),
    enabled,
    retry: false,
  });

export const useEmailConversation = (
  principal: string,
  selection?: { kind: "draft" | "message"; uuid: string },
) =>
  useInfiniteQuery({
    queryKey: emailKeys.conversation(
      principal,
      selection?.kind,
      selection?.uuid,
    ),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      emailClient.getConversation(selection!.kind, selection!.uuid, pageParam),
    getNextPageParam: (page) => page.nextBefore ?? undefined,
    select: (data) => ({
      ...data.pages[0],
      messages: [
        ...new Map(
          data.pages
            .flatMap((page) => page.messages)
            .map((message) => [message.providerId, message]),
        ).values(),
      ],
    }),
    enabled: !!selection,
    retry: false,
    staleTime: 30000,
  });
